import { Badge, Button, Spinner } from "@radix-ui/themes";
import { CheckIcon } from "@radix-ui/react-icons";
import { useTicketValidationApi } from "@/v2/entities/ticket-validation/TicketValidation.api";
import { useConversationValidationApi } from "@/v2/entities/conversation-validation/ConversationValidation.api";
import { useMutation, useQuery } from "react-query";
import { Sources } from "@/v2/entities/tracker/Tracker.api";

interface MarkAsValidatedProps {
  interactionId: string;
  onValidated?: () => void;
  source: Sources;
}

export function MarkAsValidated({
  interactionId: interactionId,
  onValidated,
  source,
}: MarkAsValidatedProps) {
  const ticketValidationApi = useTicketValidationApi();
  const conversationValidationApi = useConversationValidationApi();

  const {
    data: ticketValidation,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["ticketValidation", interactionId],
    queryFn: () =>
      ticketValidationApi.getTicketValidation({ ticketId: interactionId }),
    retry: false,
  });

  const {
    data: conversationValidation,
    isLoading: isConversationLoading,
    refetch: refetchConversationValidation,
  } = useQuery({
    queryKey: ["conversationValidation", interactionId],
    queryFn: () =>
      conversationValidationApi.getConversationValidation({
        conversationId: interactionId,
      }),
    retry: false,
  });

  const { mutate: updateTicketValidation, isLoading: isUpdatingTicket } =
    useMutation({
      mutationFn: (isValidated: boolean) =>
        ticketValidationApi.updateTicketValidation({
          ticketId: interactionId,
          isValidated,
        }),
      onSuccess: () => {
        refetch();
        onValidated?.();
      },
    });

  const {
    mutate: updateConversationValidation,
    isLoading: isUpdatingConversation,
  } = useMutation({
    mutationFn: (isValidated: boolean) =>
      conversationValidationApi.updateConversationValidation({
        conversationId: interactionId,
        isValidated,
      }),
    onSuccess: () => {
      refetchConversationValidation();
      onValidated?.();
    },
  });

  const handleMarkAsValidated = () => {
    if (source === "tickets") {
      updateTicketValidation(true);
    } else {
      updateConversationValidation(true);
    }
  };

  if (isLoading) {
    return <></>;
  }

  if (
    (source === "tickets" && ticketValidation?.isValidated) ||
    (source === "conversations" && conversationValidation?.isValidated)
  ) {
    return (
      <Badge size={"2"} color={"green"} variant={"surface"}>
        <CheckIcon />
        Validated
      </Badge>
    );
  }

  return (
    <Button
      color={"gray"}
      variant={"solid"}
      size={"1"}
      highContrast
      onClick={handleMarkAsValidated}
      disabled={isUpdatingTicket || isUpdatingConversation}
    >
      <Spinner loading={isUpdatingTicket || isUpdatingConversation}>
        <CheckIcon />
      </Spinner>
      Mark as Validated
    </Button>
  );
}
